// frontend/src/components/paywall/RedemptionCodeInput.tsx

import React, { useState } from 'react';
import { apiService } from '../../services/api';

export interface RedemptionCodeInputProps {
  /**
   * Callback when code is successfully redeemed
   */
  onSuccess?: (result: {
    planId: string;
    creditsGranted: number;
    expiresAt: string | null;
    message: string;
  }) => void;
  /**
   * Callback when redemption fails
   */
  onError?: (error: string) => void;
}

/**
 * RedemptionCodeInput Component
 * Allows users to enter and redeem a promotional code for Pro access
 */
export const RedemptionCodeInput: React.FC<RedemptionCodeInputProps> = ({
  onSuccess,
  onError,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    // Clear states when collapsing
    if (isExpanded) {
      setCode('');
      setError(null);
      setSuccess(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError('Please enter a code');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.redeemAccessCode(trimmedCode);

      if (response.success && response.data) {
        setSuccess(response.data.message);
        setCode('');
        onSuccess?.(response.data);
      } else {
        const errorMessage = response.error || 'Failed to redeem code';
        setError(errorMessage);
        onError?.(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to redeem code. Please try again.';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="redemption-code-section">
      {/* Divider with OR */}
      <div className="redemption-divider">
        <span className="divider-line"></span>
        <span className="divider-text">OR</span>
        <span className="divider-line"></span>
      </div>

      {/* Toggle link */}
      <button
        type="button"
        className="redemption-toggle"
        onClick={handleToggle}
        aria-expanded={isExpanded}
      >
        {isExpanded ? 'Hide promo code' : 'Have a promo code?'}
      </button>

      {/* Expandable input section */}
      {isExpanded && (
        <form className="redemption-form" onSubmit={handleSubmit}>
          <div className="redemption-input-group">
            <input
              type="text"
              className="redemption-input"
              placeholder="Enter code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={isLoading}
              maxLength={20}
              aria-label="Promo code"
            />
            <button
              type="submit"
              className="redemption-button"
              disabled={isLoading || !code.trim()}
            >
              {isLoading ? 'Applying...' : 'Apply'}
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="redemption-error" role="alert">
              {error}
            </div>
          )}

          {/* Success message */}
          {success && (
            <div className="redemption-success" role="status">
              {success}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default RedemptionCodeInput;
